import React, { useEffect, useRef } from 'react';
import { X, Youtube, MessageCircle, Lightbulb, Music, Building2, Newspaper } from 'lucide-react';
import { Button } from './ui/button';

// Import tab components
import VideosBoardTab from './ProducerCorner/VideosBoardTab';
import ProducerForumTab from './ProducerCorner/ProducerForumTab';
import ProTipsTab from './ProducerCorner/ProTipsTab';
import DemoDropsTab from './ProducerCorner/DemoDropsTab';
import LabelConnectTab from './ProducerCorner/LabelConnectTab';
import ConnectTab from './ProducerCorner/ConnectTab';
import ProducerNewsTab from './ProducerCorner/ProducerNewsTab';
import ProducerToolsPanel from './ProducerCorner/ProducerToolsPanel';

// Feature configuration for Producer's Corner - Purple/Gold Theme
const PRODUCER_FEATURES = {
  videos: {
    id: 'videos',
    title: 'Video Board',
    icon: Youtube,
    component: VideosBoardTab
  },
  forum: {
    id: 'forum',
    title: 'Producer Forum',
    icon: MessageCircle,
    component: ProducerForumTab
  },
  tips: {
    id: 'tips',
    title: 'Pro Tips & Producer Tools',
    icon: Lightbulb,
    component: ProducerToolsPanel
  },
  demos: {
    id: 'demos',
    title: 'Demo Drops & Contests',
    icon: Music,
    component: DemoDropsTab
  },
  labels: {
    id: 'labels',
    title: 'Connect',
    icon: Building2,
    component: ConnectTab
  },
  news: {
    id: 'news',
    title: 'Producer News',
    icon: Newspaper,
    component: ProducerNewsTab
  }
};

const ProducerCornerFeatureModal = ({ isOpen, onClose, featureId, onOpenFullModal }) => {
  const modalContentRef = useRef(null);
  
  // Get feature configuration
  const feature = PRODUCER_FEATURES[featureId];
  
  // Scroll to top when modal opens
  useEffect(() => {
    if (isOpen && modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  }, [isOpen, featureId]);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen || !feature) return null;

  const FeatureIcon = feature.icon;
  const FeatureComponent = feature.component;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        backdropFilter: 'blur(10px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}
      onClick={onClose}
    >
      {/* Modal Container with Purple/Gold Gradient Border */}
      <div 
        className="producer-feature-modal mini-modal-context"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '1200px',
          maxHeight: '90vh',
          borderRadius: '20px',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          padding: '3px',
          background: 'linear-gradient(90deg, #9333ea, #fbbf24, #9333ea, #fbbf24)',
          backgroundSize: '300% 300%',
          animation: 'producerBorderFlow 4s ease-in-out infinite'
        }}
      >
        {/* Inner content container */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          background: 'linear-gradient(135deg, rgba(15, 10, 25, 0.99), rgba(25, 15, 40, 0.99))',
          borderRadius: '17px',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1.5rem 2rem',
            borderBottom: '2px solid rgba(147, 51, 234, 0.3)',
            background: 'rgba(0, 0, 0, 0.3)'
          }}>
            <h2 style={{
              fontSize: '1.75rem',
              background: 'linear-gradient(135deg, #9333ea, #fbbf24)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <FeatureIcon size={28} style={{ color: '#fbbf24' }} />
              {feature.title}
            </h2>
            
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              style={{
                background: 'transparent',
                border: '2px solid rgba(147, 51, 234, 0.5)',
                color: '#fbbf24',
                padding: '0.5rem',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                width: '40px',
                height: '40px',
                zIndex: 10
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(147, 51, 234, 0.2)';
                e.currentTarget.style.borderColor = '#9333ea';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = 'rgba(147, 51, 234, 0.5)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <X size={20} />
            </Button>
          </div>

          {/* Content Area - Renders the actual tab component */}
          <div 
            ref={modalContentRef}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '2rem'
            }}
          >
            <FeatureComponent />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes producerBorderFlow {
          0%, 100% { 
            background-position: 0% 50%; 
          }
          50% { 
            background-position: 100% 50%; 
          }
        }

        /* Custom scrollbar for producer modal */
        .producer-feature-modal > div > div:last-child::-webkit-scrollbar {
          width: 8px;
        }

        .producer-feature-modal > div > div:last-child::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.3);
          border-radius: 10px;
        }

        .producer-feature-modal > div > div:last-child::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #9333ea, #fbbf24);
          border-radius: 10px;
        }
      `}</style>
    </div>
  );
};

export default ProducerCornerFeatureModal;
